import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMFAPreference, updateMFAPreference } from '@aws-amplify/auth';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import './Dashboard.css';
import CustomerWelcome from './CustomerWelcome';
import CustomerLicense from './CustomerLicense';
import {
  IconBell, IconUser, IconCar, IconWallet, IconChat, IconLock, IconShield, IconKey,
  IconMail, IconPhone, IconStar, IconLogout,
  IconCheck, IconSparkle, IconSend, IconMapPin,
  IconClose, IconTicket, IconArrowR,
} from './dashboard/icons';

const today = new Date();
today.setHours(0, 0, 0, 0);

function categorizeRentals(transactions) {
  const txOnly = (transactions || []).filter(t => t.type === 'transaction');
  const current = [];
  const upcoming = [];
  const past = [];
  for (const tx of txOnly) {
    const startDate = tx.start_date ? new Date(tx.start_date) : new Date(tx.event_date || tx.created_at);
    startDate.setHours(0, 0, 0, 0);
    const endDate = tx.end_date ? new Date(tx.end_date) : null;
    if (endDate) endDate.setHours(0, 0, 0, 0);

    if (startDate > today) {
      upcoming.push(tx);
    } else if (!endDate || endDate >= today) {
      current.push(tx);
    } else {
      past.push(tx);
    }
  }
  return { current, upcoming, past };
}

function totalRentalDays(transactions) {
  const txOnly = (transactions || []).filter(t => t.type === 'transaction');
  if (txOnly.length === 0) return 0;
  const earliest = txOnly.reduce((min, tx) => {
    const d = new Date(tx.start_date || tx.event_date || tx.created_at);
    return d < min ? d : min;
  }, new Date());
  return Math.max(1, Math.round((today - earliest) / (1000 * 60 * 60 * 24)));
}

function initials(first, last, email) {
  const a = (first || '').trim()[0];
  const b = (last || '').trim()[0];
  if (a || b) return `${a || ''}${b || ''}`.toUpperCase();
  return (email || '?').slice(0, 1).toUpperCase();
}

function formatMonthDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.split('T')[0] || '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  cash_app: 'Cash App',
  cashapp: 'Cash App',
  venmo: 'Venmo',
  zelle: 'Zelle',
  check: 'Check',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  card: 'Card',
  ach: 'ACH',
  bank_transfer: 'Bank Transfer',
  money_order: 'Money Order',
  paypal: 'PayPal',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  discount: 'Discount',
};

function formatPaymentMethod(method) {
  if (!method) return 'Payment';
  const key = method.toLowerCase().replace(/[- ]/g, '_');
  if (PAYMENT_METHOD_LABELS[key]) return PAYMENT_METHOD_LABELS[key];
  // fallback: title-case each word
  return method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatMonthYear(iso) {
  if (!iso) return '—';
  const [year, month] = iso.split('T')[0].split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  if (!year || !month || !months[month - 1]) return '—';
  return `${months[month - 1]} ${year}`;
}

function leaseStatus(rto) {
  if (rto.delinquency_status === 'late') {
    return { className: 'past_due', label: 'Past due' };
  }
  return {
    className: rto.status || 'current',
    label: rto.status || 'current',
  };
}

const HeroBlobs = () => (
  <svg className="tg-hero-blob" viewBox="0 0 400 240" preserveAspectRatio="none">
    <defs>
      <radialGradient id="tg-b1" cx="80%" cy="20%" r="50%">
        <stop offset="0%" stopColor="#2BD4FF" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#2BD4FF" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="tg-b2" cx="10%" cy="90%" r="60%">
        <stop offset="0%" stopColor="#1E5CFF" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#1E5CFF" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="320" cy="40" r="100" fill="url(#tg-b1)" />
    <circle cx="40" cy="220" r="140" fill="url(#tg-b2)" />
    <circle cx="260" cy="180" r="60" fill="#FFFFFF" opacity="0.18" />
  </svg>
);

function Hero({ firstName, metrics }) {
  const rentalDays = metrics.rentalDays ?? 0;
  const balance = metrics.balance ?? 0;
  const nextDue = metrics.nextDue;
  return (
    <div className="tg-hero">
      <HeroBlobs />
      <div className="tg-hero-greet">
        <span className="tg-pulse"></span> Welcome back
      </div>
      <h1 className="tg-hero-name">
        Hi, <em>{firstName || 'there'}</em>.<br />Your garage is ready.
      </h1>
      <div className="tg-hero-pills">
        <span className="tg-pill">
          <span className="tg-pill-dot"></span>
          {rentalDays} {rentalDays === 1 ? 'day' : 'days'} as a Tegrat customer
        </span>
        {nextDue && (
          <span className="tg-pill warn">
            <span className="tg-pill-dot"></span> Payment due {nextDue}
          </span>
        )}
      </div>
      <div className="tg-hero-metrics">
        <div className="tg-metric">
          <div className="tg-metric-label">Customer for</div>
          <div className="tg-metric-value">
            {rentalDays}
            <small>days</small>
          </div>
        </div>
        <div className="tg-metric alt">
          <div className="tg-metric-label">Balance</div>
          <div className="tg-metric-value">${balance.toFixed(2)}</div>
        </div>
        <div className="tg-metric">
          <div className="tg-metric-label">Next due</div>
          <div className="tg-metric-value" style={{ fontSize: 15 }}>
            {nextDue || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBar({ active, onChange, messageCount }) {
  const tabs = [
    { id: 'profile', label: 'Profile', icon: IconUser },
    { id: 'ai', label: 'Ask AI', icon: IconSparkle, isNew: true },
    { id: 'rentals', label: 'Rentals', icon: IconCar },
    { id: 'payments', label: 'Payments', icon: IconWallet },
    { id: 'messages', label: 'Messages', icon: IconChat, count: messageCount },
  ];
  return (
    <div className="tg-tabbar">
      <div className="tg-tabbar-track">
        {tabs.map(t => {
          const Ic = t.icon;
          return (
            <button
              key={t.id}
              className={`tg-tab ${active === t.id ? 'active' : ''}`}
              onClick={() => onChange(t.id)}
              type="button"
            >
              <Ic size={15} /> {t.label}
              {t.isNew && <span className="tg-tab-new-badge">NEW</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Voucher Modal ─────────────────────────────────────────────
function VoucherModal({ shop, onClose, apiCall, defaultPhone }) {
  const [offer, setOffer] = useState(shop.offers?.length === 1 ? shop.offers[0] : null);
  const [phone, setPhone] = useState(defaultPhone || '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!offer || !phone.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiCall(`${API_BASE_URL}/integrations/car-service/voucher`, {
        method: 'POST',
        body: JSON.stringify({ shop_id: shop.id, phone: phone.trim() }),
      });
      const data = res.data || res;
      setCode(data.code || '----');
    } catch (err) {
      setError(err.message || 'Could not send voucher');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tg-modal-overlay" onClick={onClose}>
      <div className="tg-modal" onClick={e => e.stopPropagation()} role="dialog">
        <button className="tg-modal-x" onClick={onClose} aria-label="Close">
          <IconClose size={16} />
        </button>
        {!code ? (
          <>
            <div className="tg-modal-eyebrow">
              <IconTicket size={13} /> Claim voucher
            </div>
            <h3 className="tg-modal-title">{shop.name}</h3>
            <div className="tg-modal-loc">
              <IconMapPin size={12} /> {shop.location}
            </div>
            {shop.offers?.length > 0 && (
              <>
                <div className="tg-modal-section">Select a service</div>
                <div className="tg-offer-list">
                  {shop.offers.map((o, i) => (
                    <button
                      key={o.id || i}
                      type="button"
                      className={`tg-offer-row ${offer && (offer.id === o.id) ? 'selected' : ''}`}
                      onClick={() => setOffer(o)}
                    >
                      <div>
                        <div className="t">{o.label}</div>
                        {o.notice && <div className="d">{o.notice}</div>}
                      </div>
                      <div className="tg-offer-price">
                        {(o.price === 0 || o.price == null) ? 'Free' : `$${o.price}`}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="tg-modal-section">Your phone number</div>
            <input
              className="tg-input"
              type="tel"
              placeholder="(317) 555-0199"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              autoFocus
            />
            <p className="tg-modal-note">
              We text the voucher code here. Not shared or used for marketing.
            </p>
            <div className="tg-modal-section">
              Note to shop <span style={{ opacity: 0.7 }}>(optional)</span>
            </div>
            <textarea
              className="tg-input"
              style={{ resize: 'none', marginBottom: 16 }}
              placeholder="e.g. Dropping off Thursday morning…"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
            />
            {error && <p className="tg-modal-note" style={{ color: '#8B1A2E' }}>{error}</p>}
            <button
              className="tg-btn-primary"
              onClick={submit}
              disabled={!offer || !phone.trim() || submitting}
            >
              {submitting ? 'Sending…' : <>Send voucher <IconArrowR size={14} /></>}
            </button>
          </>
        ) : (
          <div className="tg-voucher-success">
            <div className="tg-success-icon"><IconCheck size={28} /></div>
            <h3 className="tg-modal-title">You're all set</h3>
            <div className="tg-voucher-code">
              <span className="l">Voucher code</span>
              <span className="v">#{code}</span>
            </div>
            <p className="tg-modal-note">
              Texted to {phone}. Show this code at <strong>{shop.name}</strong>
              {offer ? <> for <strong>{offer.label}</strong></> : null}.
            </p>
            <button className="tg-btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI Chat tab ────────────────────────────────────────────────
const QUICK_PROMPTS = [
  'Oil leak under my car',
  'Brakes are grinding',
  'Check engine light is on',
  'Slow flat tire',
];

function ShopCard({ shop, onClaim }) {
  return (
    <div className="tg-ai-shop">
      <div className="tg-ai-shop-tag">Recommended shop</div>
      <div className="tg-ai-shop-name">{shop.name}</div>
      <div className="tg-ai-shop-loc">
        <IconMapPin size={11} /> {shop.location}
      </div>
      {shop.offers?.length > 0 && (
        <div className="tg-ai-offer-chips">
          {shop.offers.map((o, i) => (
            <span key={o.id || i} className="tg-ai-offer-chip">
              {o.label}
              <strong>{(o.price === 0 || o.price == null) ? 'Free' : `$${o.price}`}</strong>
            </span>
          ))}
        </div>
      )}
      <button className="tg-ai-claim" onClick={() => onClaim(shop)} type="button">
        <IconTicket size={14} /> Claim voucher
      </button>
    </div>
  );
}

// Progressively reveal text token-by-token so a single (non-streamed) response
// from the proxy still animates in like a live chat. Resolves when fully shown.
function revealText(text, onUpdate) {
  return new Promise(resolve => {
    const tokens = text.split(/(\s+)/);
    let i = 0;
    let acc = '';
    const step = () => {
      if (i >= tokens.length) { resolve(); return; }
      acc += tokens[i++];
      onUpdate(acc);
      setTimeout(step, 18);
    };
    step();
  });
}

function AskAITab({ firstName, customerPhone, apiCall }) {
  const [messages, setMessages] = useState(() => [{
    id: 'welcome',
    role: 'assistant',
    content: `Hey${firstName ? ` ${firstName}` : ''}, I'm Tegrat's service AI. Describe what's going on with your car and I'll match you with a trusted Indianapolis shop and a voucher.`,
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [voucherShop, setVoucherShop] = useState(null);
  const threadRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, loading, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading || streaming) return;
    setError(null);
    setInput('');
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content };
    setMessages(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await apiCall(`${API_BASE_URL}/integrations/car-service/chat`, {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, content }),
      });
      const data = res.data || res;
      if (data.session_id) setSessionId(data.session_id);
      const full = data.content || '';
      const shop = data.recommended_shop || null;
      const aId = `a-${Date.now()}`;
      setLoading(false);
      setStreaming(true);
      setMessages(m => [...m, { id: aId, role: 'assistant', content: '', shop: null }]);
      await revealText(full, partial => {
        setMessages(m => m.map(msg => (msg.id === aId ? { ...msg, content: partial } : msg)));
      });
      if (shop) {
        setMessages(m => m.map(msg => (msg.id === aId ? { ...msg, shop } : msg)));
      }
    } catch (err) {
      setError(err.message || 'Could not reach the service AI.');
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  return (
    <div className="tg-ai-chat tg-fade">
      <div className="tg-ai-chat-head">
        <div className="tg-ai-chat-head-l">
          <div className="tg-ai-avatar"><IconSparkle size={16} /></div>
          <div>
            <div className="t">Tegrat Service AI</div>
            <div className="d">
              <span className="tg-online-dot" />
              Online · usually replies in seconds
            </div>
          </div>
        </div>
      </div>

      <div className="tg-ai-thread" ref={threadRef}>
        {messages.map(m => (
          <div key={m.id} className={`tg-ai-row ${m.role}`}>
            {m.role === 'assistant' && (
              <div className="tg-ai-row-avatar"><IconSparkle size={12} /></div>
            )}
            <div className="tg-ai-bubble-wrap">
              {m.content && (
                <div className={`tg-ai-bubble ${m.role}`}>{m.content}</div>
              )}
              {m.shop && <ShopCard shop={m.shop} onClaim={setVoucherShop} />}
            </div>
          </div>
        ))}
        {loading && (
          <div className="tg-ai-row assistant">
            <div className="tg-ai-row-avatar"><IconSparkle size={12} /></div>
            <div className="tg-ai-bubble assistant typing">
              <span className="dot" /><span className="dot" /><span className="dot" />
            </div>
          </div>
        )}
        {error && (
          <div className="tg-ai-row assistant">
            <div className="tg-ai-row-avatar"><IconSparkle size={12} /></div>
            <div className="tg-ai-error">{error}</div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="tg-ai-quick">
          <div className="tg-ai-quick-label">Common issues</div>
          <div className="tg-ai-quick-row">
            {QUICK_PROMPTS.map(p => (
              <button
                key={p}
                type="button"
                className="tg-ai-issue-chip"
                onClick={() => send(p)}
              >{p}</button>
            ))}
          </div>
        </div>
      )}

      <form className="tg-ai-composer" onSubmit={e => { e.preventDefault(); send(); }}>
        <input
          type="text"
          placeholder="Describe what your car's doing…"
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={loading || streaming}
        />
        <button
          type="submit"
          className="tg-ai-send"
          disabled={!input.trim() || loading || streaming}
          aria-label="Send"
        >
          <IconSend size={16} />
        </button>
      </form>
      <div className="tg-ai-disclaimer">
        AI suggestions only — confirm pricing at the shop.
      </div>

      {voucherShop && (
        <VoucherModal
          shop={voucherShop}
          onClose={() => setVoucherShop(null)}
          apiCall={apiCall}
          defaultPhone={customerPhone}
        />
      )}
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────
export function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, apiCall, logout, loading: authLoading } = useAuth();

  const [customerData, setCustomerData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  const [payments, setPayments] = useState([]);
  const [rtoAgreements, setRtoAgreements] = useState([]);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState(null);
  const pollRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [notifPrefs, setNotifPrefs] = useState({
    notify_sms: true, notify_email: true, notify_promotions: false,
    notify_rental_updates: true, notify_payment_reminders: true,
  });
  const [mfaStatus, setMfaStatus] = useState(null);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [emailMfaLoading, setEmailMfaLoading] = useState(false);
  const [disableMfaLoading, setDisableMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState(null);

  const [showSmsConsent, setShowSmsConsent] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);

  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    oldPassword: '', newPassword: '', confirmPassword: '',
  });
  const [passwordMessage, setPasswordMessage] = useState(null);

  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState(null);
  const handleUnlink = async () => {
    setUnlinkBusy(true); setUnlinkError(null);
    try {
      await apiCall(`${API_BASE_URL}/auth/phone-link/unlink`, { method: 'POST' });
      window.location.replace('/dashboard');
    } catch (err) { setUnlinkError(err.message); setUnlinkBusy(false); }
  };

  // Initial load
  useEffect(() => {
    if (user?.customerId && apiCall) {
      loadCustomerData();
      if (activeTab === 'profile') loadAccountData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, apiCall]);

  // Lazy-load per tab
  useEffect(() => {
    if (!user?.customerId) return;
    if (activeTab === 'payments' && !paymentsLoaded) loadPayments();
    if (activeTab === 'messages') loadMessages();
    if (activeTab === 'profile' && !accountLoaded && user) loadAccountData();

    if (activeTab === 'messages') {
      pollRef.current = setInterval(() => loadMessages(true), 30000);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      const customerRequest = user.customerId
        ? apiCall(`${API_BASE_URL}/customers/${user.customerId}`, { method: 'GET' })
        : Promise.resolve({ data: null });
      const [customerRes, txRes, rtoRes] = await Promise.all([
        customerRequest,
        apiCall(`${API_BASE_URL}/customers/${user.customerId}/history`, { method: 'GET' }),
        apiCall(`${API_BASE_URL}/rto-agreements?customer_id=${user.customerId}`, { method: 'GET' }),
      ]);
      setCustomerData(customerRes.data);
      setTransactions(txRes.data || []);
      // Load leases with the dashboard so they are visible in Rentals and the
      // hero's next-payment metric does not wait for the Payments tab to open.
      setRtoAgreements(rtoRes.data || []);
    } catch (e) {
      console.error('Failed to load customer data:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      const pRes = await apiCall(`${API_BASE_URL}/payments?customer_id=${user.customerId}`, { method: 'GET' });
      setPayments(pRes.data || []);
      setPaymentsLoaded(true);
    } catch (e) {
      console.error('Failed to load payments:', e);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadMessages = useCallback(async (silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const res = await apiCall(
        `${API_BASE_URL}/customers/${user.customerId}/communication`,
        { method: 'GET' }
      );
      setMessages([...(res.data || [])].reverse());
      setMessagesLoaded(true);
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      if (!silent) setMessagesLoading(false);
    }
  }, [apiCall, user]);

  const loadAccountData = async () => {
    setAccountLoading(true);
    try {
      const [prefsRes, mfaPrefs] = await Promise.all([
        apiCall(`${API_BASE_URL}/customers/${user.customerId}/preferences`, { method: 'GET' }),
        fetchMFAPreference().catch(() => null),
      ]);
      setNotifPrefs(prev => ({ ...prev, ...(prefsRes.data || {}) }));
      setMfaStatus(mfaPrefs);
      setAccountLoaded(true);
    } catch (e) {
      console.error('Failed to load account data:', e);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    setMessageError(null);
    setSendingMessage(true);
    try {
      await apiCall(`${API_BASE_URL}/communications`, {
        method: 'POST',
        body: JSON.stringify({ customer_id: user.customerId, content: messageInput.trim() }),
      });
      setMessageInput('');
      await loadMessages(true);
    } catch (err) {
      setMessageError(err.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const saveNotifToggle = async (field, value, smsConsent) => {
    const prev = notifPrefs;
    const updated = { ...notifPrefs, [field]: value };
    setNotifPrefs(updated);
    try {
      const payload = smsConsent ? { ...updated, sms_consent: smsConsent } : updated;
      const res = await apiCall(`${API_BASE_URL}/customers/${user.customerId}/preferences`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (res.data) setNotifPrefs(prevP => ({ ...prevP, ...res.data }));
    } catch (e) {
      setNotifPrefs(prev);
      console.error('Failed to save preferences:', e);
    }
  };

  const handleNotifToggle = (field) => {
    if (field === 'notify_sms' && !notifPrefs.notify_sms) {
      setSmsConsentChecked(false);
      setShowSmsConsent(true);
    } else {
      saveNotifToggle(field, !notifPrefs[field]);
    }
  };

  const handleSmsConsentConfirm = async () => {
    setShowSmsConsent(false);
    await saveNotifToggle('notify_sms', true, { agreed: true, version: 'v1_portal' });
  };

  const totpOn = mfaStatus?.enabled?.includes('TOTP') ?? false;
  const emailMfaOn = mfaStatus?.enabled?.includes('EMAIL') ?? false;
  const mfaActive = totpOn || emailMfaOn;

  const handleToggleEmailMfa = async () => {
    setMfaError(null);
    setEmailMfaLoading(true);
    try {
      await updateMFAPreference({ email: emailMfaOn ? 'DISABLED' : 'ENABLED' });
      const updated = await fetchMFAPreference();
      setMfaStatus(updated);
    } catch (err) {
      setMfaError(err.message || 'Failed to update Email MFA');
    } finally {
      setEmailMfaLoading(false);
    }
  };

  const handleDisableAllMfa = async () => {
    setMfaError(null);
    setDisableMfaLoading(true);
    try {
      await updateMFAPreference({ totp: 'DISABLED', email: 'DISABLED' });
      const updated = await fetchMFAPreference();
      setMfaStatus(updated);
    } catch (err) {
      setMfaError(err.message || 'Failed to disable MFA');
    } finally {
      setDisableMfaLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);
    const { oldPassword, newPassword, confirmPassword } = changePasswordData;
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    try {
      const response = await apiCall(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      if (response.success) {
        setPasswordMessage({ type: 'success', text: 'Password changed!' });
        setChangePasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setShowChangePasswordForm(false), 1800);
      } else {
        setPasswordMessage({ type: 'error', text: response.error || 'Failed to change password' });
      }
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err.message || 'Error changing password' });
    }
  };

  if (authLoading || !user || (loading && user?.customerId)) {
    return (
      <div className="tg-dash">
        <div className="tg-shell">
          <div className="tg-loading">Loading your dashboard…</div>
        </div>
      </div>
    );
  }

  if (user && !user.customerId) {
    return <CustomerWelcome user={user} logout={logout} onConnected={async () => {
      // Reload to discard every cache from the previous rental account.
      window.location.replace('/dashboard');
    }} />;
  }

  const { current: currentRentals, upcoming: upcomingRentals, past: pastRentals } =
    categorizeRentals(transactions);
  const activeLeases = rtoAgreements.filter(rto => rto.status === 'active');
  const pastLeases = rtoAgreements.filter(rto => rto.status !== 'active');

  const firstName =
    user?.firstName || customerData?.first_name ||
    (user?.email ? user.email.split('@')[0] : '');
  const lastName = user?.lastName || customerData?.last_name || '';
  const customerEmail = customerData?.email || user?.email || '';
  const customerPhone = customerData?.phone_number || user?.phone || '';
  const memberSince = formatMonthYear(customerData?.created_at);

  const balance = parseFloat(customerData?.wallet?.current_balance || 0);
  const nextDuePayment = rtoAgreements
    .map(r => r.next_payment_due_date)
    .filter(Boolean)
    .sort()[0];
  const metrics = {
    rentalDays: totalRentalDays(transactions),
    balance,
    nextDue: balance > 0 ? 'Now' : (nextDuePayment ? formatMonthDay(nextDuePayment) : null),
  };

  const unreadMessageCount = messages.filter(
    m => m.direction === 'OUTBOUND' && !m.read_at
  ).length || undefined;

  return (
    <div className="tg-dash">
      <div className="tg-shell">
        <header className="tg-topbar">
          <div className="tg-topbar-row">
            <div className="tg-brand">
              <span className="tg-brand-mark">T</span> Tegrat Auto
            </div>
            <div className="tg-topbar-right">
              <span className="tg-user-chip">{customerEmail}</span>
              <button className="tg-icon-btn" type="button" aria-label="Notifications">
                <IconBell size={17} />
              </button>
            </div>
          </div>
        </header>

        <Hero firstName={firstName} metrics={metrics} />
        <TabBar
          active={activeTab}
          onChange={setActiveTab}
          messageCount={unreadMessageCount}
        />

        <main className={`tg-content tg-content--${activeTab}`} key={activeTab}>
          {activeTab === 'profile' && (
            <>
              {/* Identity */}
              <div className="tg-card tg-fade">
                <div className="tg-identity">
                  <div className="tg-avatar">{initials(firstName, lastName, customerEmail)}</div>
                  <div className="tg-identity-meta">
                    <h1>{firstName} {lastName}</h1>
                    <div className="sub"><span>Member since {memberSince}</span></div>
                  </div>
                </div>
              </div>

              {/* Personal info */}
              <div className="tg-card tg-card-pad tg-fade">
                <div className="tg-card-title">
                  <h2>Personal information</h2>
                </div>
                <div className="tg-kv-list">
                  <div className="tg-kv">
                    <span className="tg-kv-key">
                      <IconMail size={13} stroke="#6B7693" />&nbsp; Email
                    </span>
                    <span className="tg-kv-val">{customerEmail || '—'}</span>
                  </div>
                  <div className="tg-kv">
                    <span className="tg-kv-key">
                      <IconPhone size={13} stroke="#6B7693" />&nbsp; Phone
                    </span>
                    <span className="tg-kv-val">{customerPhone || '—'}</span>
                  </div>
                  <div className="tg-kv">
                    <span className="tg-kv-key">Member since</span>
                    <span className="tg-kv-val">{memberSince}</span>
                  </div>
                </div>
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className="tg-btn sm"
                    onClick={() => setShowChangePasswordForm(v => !v)}
                  >
                    {showChangePasswordForm ? <IconClose size={13} /> : <IconLock size={13} />}
                    {showChangePasswordForm ? 'Cancel' : 'Change password'}
                  </button>
                </div>

                {showChangePasswordForm && (
                  <form className="tg-form" onSubmit={handleChangePassword}>
                    {passwordMessage && (
                      <div className={`tg-form-msg ${passwordMessage.type}`}>
                        {passwordMessage.text}
                      </div>
                    )}
                    <label>Current password</label>
                    <input
                      type="password" required
                      value={changePasswordData.oldPassword}
                      onChange={e => setChangePasswordData(p => ({ ...p, oldPassword: e.target.value }))}
                    />
                    <label>New password</label>
                    <input
                      type="password" required
                      value={changePasswordData.newPassword}
                      onChange={e => setChangePasswordData(p => ({ ...p, newPassword: e.target.value }))}
                    />
                    <label>Confirm new password</label>
                    <input
                      type="password" required
                      value={changePasswordData.confirmPassword}
                      onChange={e => setChangePasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                    />
                    <button type="submit" className="tg-btn primary sm" style={{ alignSelf: 'flex-start' }}>
                      Change password
                    </button>
                  </form>
                )}
              </div>

              {customerData && <CustomerLicense key={user.customerId} number={customerData?.driver_license_number}
                state={customerData?.driver_license_state} international={customerData?.international_license} apiCall={apiCall} />}
              <section className="tg-card tg-card-pad">
                <div className="tg-card-title"><h2>Linked rental account</h2></div>
                <p>Account #{user.customerId} · {customerPhone}</p>
                <p>Unlinking removes this login’s rental access. Your rental records and other linked logins stay in place. You can link again with a new phone verification.</p>
                {unlinkError && <p role="alert" className="tg-form-msg error">{unlinkError}</p>}
                {confirmUnlink ? <>
                  <p>Unlink this rental account?</p>
                  <button type="button" className="tg-btn" disabled={unlinkBusy} onClick={handleUnlink}>{unlinkBusy ? 'Unlinking…' : 'Confirm unlink'}</button>
                  <button type="button" className="tg-btn" disabled={unlinkBusy} onClick={() => setConfirmUnlink(false)}>Cancel</button>
                </> : <button type="button" className="tg-btn" onClick={() => setConfirmUnlink(true)}>Unlink rental account</button>}
              </section>

              {/* Security */}
              <div className="tg-card tg-card-pad tg-fade">
                <div className="tg-card-title">
                  <h2>Security</h2>
                  <span className={`tg-status-chip ${mfaActive ? 'active' : 'completed'}`}>
                    <IconShield size={10} />{mfaActive ? 'Protected' : 'Off'}
                  </span>
                </div>
                {mfaError && (
                  <div className="tg-form-msg error" style={{ marginBottom: 8 }}>
                    {mfaError}
                  </div>
                )}
                <div className="tg-setting">
                  <span className="tg-setting-icon success"><IconMail size={17} /></span>
                  <div className="tg-setting-body">
                    <div className="t">
                      Email MFA
                      {emailMfaOn && mfaStatus?.preferred === 'EMAIL' && (
                        <span className="tg-tag preferred">Preferred</span>
                      )}
                    </div>
                    <div className="d">One-time code emailed at login</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${emailMfaOn ? 'on' : ''}`}
                    onClick={handleToggleEmailMfa}
                    disabled={emailMfaLoading}
                    aria-label="Email MFA"
                  />
                </div>
                <div className="tg-setting">
                  <span className="tg-setting-icon citrus"><IconKey size={17} /></span>
                  <div className="tg-setting-body">
                    <div className="t">
                      Authenticator app
                      {totpOn && mfaStatus?.preferred === 'TOTP' && (
                        <span className="tg-tag preferred">Preferred</span>
                      )}
                    </div>
                    <div className="d">Use Google Authenticator, 1Password, etc.</div>
                  </div>
                  <button type="button" className="tg-btn sm" onClick={() => navigate('/mfa-setup')}>
                    {totpOn ? 'Reconfigure' : 'Set up'}
                  </button>
                </div>
                {mfaActive && (
                  <div className="tg-setting">
                    <span className="tg-setting-icon"><IconLock size={17} /></span>
                    <div className="tg-setting-body">
                      <div className="t">Disable all MFA</div>
                      <div className="d">Turn off every second factor on this account.</div>
                    </div>
                    <button
                      type="button"
                      className="tg-btn sm danger"
                      onClick={handleDisableAllMfa}
                      disabled={disableMfaLoading}
                    >
                      {disableMfaLoading ? 'Disabling…' : 'Disable'}
                    </button>
                  </div>
                )}
              </div>

              {/* Notifications */}
              <div className="tg-card tg-card-pad tg-fade">
                <div className="tg-card-title"><h2>Notifications</h2></div>
                <div className="tg-eyebrow"><h3>Methods</h3></div>
                <div className="tg-setting">
                  <span className="tg-setting-icon"><IconPhone size={16} /></span>
                  <div className="tg-setting-body">
                    <div className="t">SMS text messages</div>
                    <div className="d">Rental updates and payment alerts</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${notifPrefs.notify_sms ? 'on' : ''}`}
                    onClick={() => handleNotifToggle('notify_sms')}
                  />
                </div>
                <div className="tg-setting">
                  <span className="tg-setting-icon"><IconMail size={16} /></span>
                  <div className="tg-setting-body">
                    <div className="t">Email</div>
                    <div className="d">Receipts, statements, account info</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${notifPrefs.notify_email ? 'on' : ''}`}
                    onClick={() => handleNotifToggle('notify_email')}
                  />
                </div>
                <div className="tg-eyebrow" style={{ marginTop: 12 }}><h3>Topics</h3></div>
                <div className="tg-setting">
                  <span className="tg-setting-icon citrus"><IconCar size={16} /></span>
                  <div className="tg-setting-body">
                    <div className="t">Rental updates</div>
                    <div className="d">Pickups, returns, vehicle status</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${notifPrefs.notify_rental_updates ? 'on' : ''}`}
                    onClick={() => handleNotifToggle('notify_rental_updates')}
                  />
                </div>
                <div className="tg-setting">
                  <span className="tg-setting-icon warn"><IconWallet size={16} /></span>
                  <div className="tg-setting-body">
                    <div className="t">Payment reminders</div>
                    <div className="d">Upcoming and overdue payments</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${notifPrefs.notify_payment_reminders ? 'on' : ''}`}
                    onClick={() => handleNotifToggle('notify_payment_reminders')}
                  />
                </div>
                <div className="tg-setting">
                  <span className="tg-setting-icon"><IconStar size={16} /></span>
                  <div className="tg-setting-body">
                    <div className="t">Promotions & offers</div>
                    <div className="d">Member discounts and seasonal deals</div>
                  </div>
                  <button
                    type="button"
                    className={`tg-toggle ${notifPrefs.notify_promotions ? 'on' : ''}`}
                    onClick={() => handleNotifToggle('notify_promotions')}
                  />
                </div>
              </div>

              {accountLoading && <div className="tg-loading">Loading settings…</div>}

              <button
                type="button"
                className="tg-btn ghost danger"
                style={{ alignSelf: 'center', marginTop: 4 }}
                onClick={logout}
              >
                <IconLogout size={14} /> Sign out
              </button>
            </>
          )}

          {activeTab === 'ai' && (
            <AskAITab
              firstName={firstName}
              customerPhone={customerPhone}
              apiCall={apiCall}
            />
          )}

          {activeTab === 'rentals' && (
            <>
              {(activeLeases.length > 0 || pastLeases.length > 0) && (
                <>
                  <div className="tg-eyebrow"><h3>Long-term leases</h3></div>
                  {[{ label: 'Active leases', items: activeLeases }, { label: 'Past leases', items: pastLeases }]
                    .filter(({ items }) => items.length > 0)
                    .map(({ label, items }) => (
                      <div key={label} className="tg-card tg-card-pad tg-fade">
                        <div className="tg-card-title"><h2>{label}</h2></div>
                        {items.map(rto => {
                          const status = leaseStatus(rto);
                          const startDate = rto.start_date ? rto.start_date.split('T')[0] : null;
                          return (
                            <div key={rto.id} className="tg-list-row">
                              <div className="meta">
                                <div className="t">
                                  {rto.vehicle_year} {rto.vehicle_make} {rto.vehicle_model}
                                </div>
                                <div className="d">
                                  {rto.vehicle_license_plate && (
                                    <span style={{ marginRight: 6 }}>{rto.vehicle_license_plate}</span>
                                  )}
                                  {startDate ? `Started ${startDate}` : 'Long-term lease'}
                                  {rto.next_payment_due_date && ` · Next due ${rto.next_payment_due_date.split('T')[0]}`}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <span className={`tg-status-chip ${status.className}`}>{status.label}</span>
                                <div className="amt" style={{ marginTop: 5 }}>
                                  ${parseFloat(rto.balance_remaining || 0).toFixed(2)}
                                  <small>remaining</small>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </>
              )}
              {[
                { label: 'Current', items: currentRentals, status: 'active', emptyMsg: 'No current rentals' },
                { label: 'Upcoming', items: upcomingRentals, status: 'upcoming', emptyMsg: 'No upcoming rentals' },
                { label: 'Past', items: pastRentals, status: 'completed', emptyMsg: 'No past rentals' },
              ].map(({ label, items, status, emptyMsg }) => (
                <div key={label} className="tg-card tg-card-pad tg-fade">
                  <div className="tg-card-title">
                    <h2>{label}</h2>
                    {label === 'Current' && items.length > 0 && (
                      <span className="tg-status-chip active">
                        <IconCheck size={10} />active
                      </span>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <div className="tg-empty">{emptyMsg}</div>
                  ) : (
                    items.map((tx, idx) => {
                      const startStr = tx.start_date
                        ? tx.start_date.split('T')[0]
                        : (tx.event_date || tx.created_at || '').split('T')[0];
                      const endStr = tx.end_date ? tx.end_date.split('T')[0] : null;
                      const title = tx.vehicle_label || tx.description || 'Rental';
                      return (
                        <div key={idx} className="tg-list-row">
                          <div className="meta">
                            <div className="t">{title}</div>
                            <div className="d">
                              {tx.vehicle_license_plate && (
                                <span style={{ marginRight: 6 }}>{tx.vehicle_license_plate}</span>
                              )}
                              {endStr ? `${startStr} – ${endStr}` : startStr}
                              {tx.notes ? ` · ${tx.notes}` : ''}
                            </div>
                          </div>
                          <div className="amt">${parseFloat(tx.amount || 0).toFixed(2)}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </>
          )}

          {activeTab === 'payments' && (
            <>
              {paymentsLoading ? (
                <div className="tg-loading">Loading payments…</div>
              ) : (
                <>
                  <div className="tg-card tg-card-pad tg-fade">
                    <div className="tg-card-title"><h2>Account balance</h2></div>
                    <div className="tg-stat-strip">
                      <div>
                        <div className="l">Total billed</div>
                        <div className="v">
                          ${parseFloat(customerData?.wallet?.total_billed || 0).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="l">Balance</div>
                        <div className="v" style={{
                          color: balance > 0 ? 'var(--crimson)' : 'var(--mint)',
                        }}>
                          ${balance.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {rtoAgreements.length > 0 && (
                    <>
                      <div className="tg-eyebrow"><h3>Lease agreements</h3></div>
                      {rtoAgreements.map(rto => (
                        <div key={rto.id} className="tg-card tg-card-pad tg-fade">
                          <div className="tg-card-title">
                            <h2>
                              {rto.vehicle_year} {rto.vehicle_make} {rto.vehicle_model}
                            </h2>
                            <span className={`tg-status-chip ${rto.delinquency_status || 'current'}`}>
                              {rto.delinquency_status || 'current'}
                            </span>
                          </div>
                          <div className="tg-kv-list">
                            <div className="tg-kv">
                              <span className="tg-kv-key">Plate</span>
                              <span className="tg-kv-val">{rto.vehicle_license_plate}</span>
                            </div>
                            <div className="tg-kv">
                              <span className="tg-kv-key">Balance remaining</span>
                              <span
                                className="tg-kv-val num"
                                style={{
                                  color: parseFloat(rto.balance_remaining) > 0
                                    ? 'var(--crimson)' : 'var(--mint)',
                                }}
                              >
                                ${parseFloat(rto.balance_remaining || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="tg-kv">
                              <span className="tg-kv-key">Payment</span>
                              <span className="tg-kv-val">
                                ${parseFloat(rto.recurring_amount || 0).toFixed(2)} / {rto.frequency}
                              </span>
                            </div>
                            <div className="tg-kv">
                              <span className="tg-kv-key">Next due</span>
                              <span className="tg-kv-val">
                                {rto.next_payment_due_date ? rto.next_payment_due_date.split('T')[0] : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  <div className="tg-card tg-card-pad tg-fade">
                    <div className="tg-card-title">
                      <h2>Recent payments</h2>
                    </div>
                    {payments.length === 0 ? (
                      <div className="tg-empty">No payment history found.</div>
                    ) : (
                      payments.map(p => (
                        <div key={p.id} className="tg-list-row">
                          <span className="tg-setting-icon" style={{
                            background: 'var(--mint-soft)', color: '#075F56',
                          }}>
                            <IconCheck size={16} />
                          </span>
                          <div className="meta">
                            <div className="t">{formatPaymentMethod(p.payment_method)}</div>
                            <div className="d">
                              {(p.payment_date || p.created_at || '').split('T')[0]}
                              {p.reference_number ? ` · ${p.reference_number}` : ''}
                            </div>
                          </div>
                          <div className="amt">${parseFloat(p.amount || 0).toFixed(2)}</div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'messages' && (
            <div className="tg-card tg-card-pad tg-fade">
              <div className="tg-card-title"><h2>Messages</h2></div>
              {messagesLoading && !messagesLoaded ? (
                <div className="tg-loading">Loading messages…</div>
              ) : (
                <>
                  <div className="tg-msg-thread">
                    {messages.length === 0 ? (
                      <div className="tg-msg-empty">
                        No messages yet. Send one to your rental office below.
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isOutbound = msg.direction === 'OUTBOUND';
                        return (
                          <div key={idx} className={`tg-msg-row ${isOutbound ? 'out' : 'in'}`}>
                            <div className="tg-msg-bubble">
                              <div>{msg.content}</div>
                              <div className="tg-msg-time">
                                {isOutbound ? 'Office' : 'You'} · {(msg.created_at || '').split('T')[0]}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  {messageError && (
                    <div className="tg-form-msg error" style={{ marginBottom: 8 }}>
                      {messageError}
                    </div>
                  )}
                  <form
                    onSubmit={handleSendMessage}
                    className="tg-ai-composer"
                    style={{ border: 0, padding: 0, background: 'transparent' }}
                  >
                    <input
                      type="text"
                      value={messageInput}
                      onChange={e => setMessageInput(e.target.value)}
                      placeholder="Type a message…"
                      disabled={sendingMessage}
                    />
                    <button
                      type="submit"
                      className="tg-ai-send"
                      disabled={sendingMessage || !messageInput.trim()}
                      aria-label="Send"
                    >
                      <IconSend size={16} />
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </main>
      </div>

      {showSmsConsent && (
        <div className="tg-modal-overlay" onClick={() => setShowSmsConsent(false)}>
          <div className="tg-modal" onClick={e => e.stopPropagation()}>
            <button
              className="tg-modal-x" onClick={() => setShowSmsConsent(false)}
              aria-label="Close"
            ><IconClose size={16} /></button>
            <div className="tg-modal-eyebrow"><IconPhone size={13} /> SMS consent</div>
            <h3 className="tg-modal-title">Enable SMS notifications</h3>
            <p className="tg-modal-note">
              By enabling, you agree to receive text messages from Tegrat Auto about your
              rental status and account updates. Message frequency varies; rates may apply.
              Reply <strong>STOP</strong> to opt out.
            </p>
            <label className="tg-modal-check">
              <input
                type="checkbox"
                checked={smsConsentChecked}
                onChange={e => setSmsConsentChecked(e.target.checked)}
              />
              <span>I agree to receive SMS text messages from Tegrat Auto.</span>
            </label>
            <div className="tg-modal-foot">
              <button className="tg-btn sm" onClick={() => setShowSmsConsent(false)}>
                Cancel
              </button>
              <button
                className="tg-btn primary sm"
                disabled={!smsConsentChecked}
                onClick={handleSmsConsentConfirm}
              >Enable SMS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDashboard;
